/**
 * Where the access screens send a person - pure, so it is tested.
 */

/** The first screen after login ([F-020]); the system account's is the administration ([F-031]). */
export function landingFor(isSystem: boolean): "/admin/users" | "/dashboard" {
  return isSystem ? "/admin/users" : "/dashboard";
}

/**
 * The address the login was interrupted at, if it is one of this
 * application's own pages. Anything else - another host, a protocol-relative
 * `//host`, a `javascript:` URL, the login itself - is dropped: a link that
 * logs a person in and then hands them to a page of somebody else's choosing
 * is an open redirect.
 */
export function safeTarget(target: unknown): string | null {
  if (typeof target !== "string" || target === "") return null;
  if (!target.startsWith("/") || target.startsWith("//") || target.startsWith("/\\")) return null;
  const path = target.split(/[?#]/)[0] ?? "";
  if (PUBLIC_PATHS.includes(path)) return null;
  return target;
}

/** The pages that need no session ([F-029]). */
export const PUBLIC_PATHS = ["/anmelden", "/erste-anmeldung", "/passwort-vergessen"];

/**
 * The pages the system account uses ([F-031]: it administers accounts and
 * works on no content). Elsewhere it is sent to the administration rather
 * than shown screens whose every request the server refuses.
 */
export function systemMayOpen(pathname: string): boolean {
  return ["/admin/users", "/admin/status", "/admin/journal"].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** The login page's search: `?weiter=<the interrupted page>`. */
export function parseLoginSearch(search: Record<string, unknown>): { weiter?: string } {
  const weiter = safeTarget(search.weiter);
  return weiter ? { weiter } : {};
}

/**
 * The invitation link is `/erste-anmeldung#<account id>.<secret>` ([F-029]):
 * the secret rides in the fragment, which no server and no proxy log ever
 * sees. The account id has no dot; the secret is base64url and has none
 * either.
 */
export function readInviteLink(hash: string): { account: string; secret: string } | null {
  const match = /^#([A-Za-z0-9-]+)\.([A-Za-z0-9_-]+)$/.exec(hash);
  return match && match[1] && match[2] ? { account: match[1], secret: match[2] } : null;
}

/**
 * A session's device in a few words ([DO-013] keeps the user agent so a
 * person can tell their sessions apart). Rough on purpose: it names the
 * browser and the system, and the full string stays in the tooltip.
 */
export function describeAgent(userAgent: string): string {
  const browser = /Edg\//.test(userAgent)
    ? "Edge"
    : /Firefox\//.test(userAgent)
      ? "Firefox"
      : /Chrome\//.test(userAgent)
        ? "Chrome"
        : /Safari\//.test(userAgent)
          ? "Safari"
          : null;
  const system = /Windows/.test(userAgent)
    ? "Windows"
    : /Android/.test(userAgent)
      ? "Android"
      : /iPhone|iPad/.test(userAgent)
        ? "iOS"
        : /Mac OS X/.test(userAgent)
          ? "macOS"
          : /Linux/.test(userAgent)
            ? "Linux"
            : null;
  if (browser && system) return `${browser} (${system})`;
  return browser ?? system ?? (userAgent.trim() || "-");
}
